import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { Component, inject, Inject, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { AuthService } from '~features/auth/auth.service';
import { CookieService } from '~features/cookies/cookie.service';
import { UserBehaviorAnalyticsService } from '~features/user-behavior-analytics/user-behavior-analytics.service';
import { ContactBannerComponent } from '~features/contact-banner/contact-banner.component';
import { BRAND } from '~global-constants/brand';

export interface SignupInfoDialogData {
  data: {
    centered: boolean;
    size: string;
    status: string;
  };
}

@Component({
  selector: 'app-signup-info-dialog',
  standalone: true,
  imports: [ReactiveFormsModule, FormsModule, ContactBannerComponent],
  templateUrl: './signup-info-dialog.component.html',
})
export class SignupInfoDialogComponent implements OnInit {
  readonly brand = BRAND;
  constructor(
    public dialogRef: DialogRef<string>,
    @Inject(DIALOG_DATA) public data: SignupInfoDialogData
  ) {}

  private authService = inject(AuthService);
  private router = inject(Router);
  private cookieService = inject(CookieService);
  private uabService = inject(UserBehaviorAnalyticsService);
  loginData = {
    userName: '',
    password: '',
  };

  showError = false;
  isLoading = false;

  ngOnInit() {
    // Track when signup dialog is shown
    this.uabService.trackEvent('signup_dialog_shown', {
      status: this.data.data.status,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    });
  }

  async login() {
    // Track login attempt
    this.uabService.trackEvent('signup_dialog_login_attempt', {
      email: this.loginData.userName,
      timestamp: new Date().toISOString(),
      url: window.location.href,
    });

    this.showError = false;
    this.isLoading = true;

    try {
      const loginResult = await this.authService.login(this.loginData);
      this.authService.temporaryEmailInput = '';

      // Confirmation status is now handled by the banner component

      // Track successful login
      this.uabService.trackEvent('signup_dialog_login_success', {
        email: this.loginData.userName,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        confirmed: loginResult.confirmed,
      });

      this.dialogRef.close();
    } catch (err) {
      this.showError = true;
      this.authService.temporaryEmailInput = this.loginData.userName;

      // Track failed login
      this.uabService.trackEvent('signup_dialog_login_failed', {
        email: this.loginData.userName,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        error: err,
      });
    } finally {
      this.isLoading = false;
    }
  }

  goToSignup() {
    // Track signup button click
    this.uabService.trackEvent('signup_dialog_signup_clicked', {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      destination: '/signup',
    });

    this.dialogRef.close();
    this.router.navigate(['signup']);
  }

  goToRequestPassword() {
    // Track password reset click
    this.uabService.trackEvent('signup_dialog_forgot_password_clicked', {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      destination: '/requestnewpassword',
    });

    this.dialogRef.close();
    this.router.navigate(['requestnewpassword']);
  }

  continueWithoutAccount() {
    // Track continue without account click
    this.uabService.trackEvent('signup_dialog_continue_without_account', {
      timestamp: new Date().toISOString(),
      url: window.location.href,
      action: 'bypass_signup',
    });

    this.cookieService.setFullCookiePermissions();
    this.dialogRef.close();
  }
}
